import {FC} from 'react'

import {JurisdictionTooltipRow} from '@/components/common/jurisdiction/JurisdictionTooltipRow'
import {usePreferences} from '@/components/context/Preferences'
import {useRenewalCategoryFeeRows} from '@/components/trademark-renewal/form/hooks'
import {useFormSummaryContent} from '@/content/formSummary'
import {useTranslation} from '@/i18n'
import {Jurisdiction} from '@/utils/countries/constants'
import {includedCategoryClasses} from '@/utils/pricing'
import {
  calculateTmRenewalJurisdictionPrice,
  getRenewalCategoryFees,
} from '@/utils/pricing/renewal/calculations'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type JurisdictionTooltipProps = {
  jurisdiction: Jurisdiction
  hasPriceBreakdown?: boolean
}

export const JurisdictionTooltip: FC<React.PropsWithChildren<JurisdictionTooltipProps>> = ({
  jurisdiction,
  hasPriceBreakdown = false,
}) => {
  const {t} = useTranslation()
  const {serviceFeeTitle, administrativeFeeTitle, includedClassesTitle} = useFormSummaryContent()
  const {currency} = usePreferences()
  const categoryFees = getRenewalCategoryFees(jurisdiction, currency)
  const jurisdictionPrice = calculateTmRenewalJurisdictionPrice(jurisdiction, currency)
  const feeRows = useRenewalCategoryFeeRows(categoryFees)

  return (
    <>
      <div className="breakdown">
        {hasPriceBreakdown && (
          <>
            <JurisdictionTooltipRow
              title={t(serviceFeeTitle)}
              number={jurisdictionPrice.legalFee.total.vatBase}
            />
            <JurisdictionTooltipRow
              title={t(administrativeFeeTitle)}
              number={jurisdictionPrice.registrationFee.total.vatBase}
            />
          </>
        )}
        <JurisdictionTooltipRow
          title={t(includedClassesTitle)}
          number={includedCategoryClasses[jurisdiction]}
          numberType="number"
        />
        {feeRows.map((row, i) => (
          <JurisdictionTooltipRow key={i} title={row.title} number={row.price} note={row.note} />
        ))}
      </div>

      <style jsx>{`
        @media ${TABLET_SCREENSIZE} {
          .breakdown {
            min-width: 350px;
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .breakdown {
            min-width: inherit;
          }
        }
      `}</style>
    </>
  )
}
